import { createAsyncThunk } from "@reduxjs/toolkit";
import { postData } from "../../services/apis";
import { setList, setDetails } from "./SalesRecordSlice";

export const fetchSalesRecord = createAsyncThunk(
  "salesRecord/fetchSalesRecord",
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const selFilter =
        payload?.selectedFilterType || payload?.filters?.filterType || "All";
      let selRecord =
        payload?.selectedRecordType ||
        payload?.filters?.recordType ||
        "without item";

      // Select endpoint
      let url = "/sales/get-sales-record";
      if (selRecord === "with item")
        url = "/sales/get-sales-record-with-details";
      if (
        ["By Item", "By Tag", "By Group", "By Category"].includes(selFilter)
      ) {
        url = "/sales/get-sales-details";
        selRecord = "without item";
      }

      const reqPayload = {
        ...payload.filters,
        selectedFilterType: selFilter,
        selectedRecordType: selRecord,
      };

      const res = await postData(url, reqPayload);

      if (!res || res?.error)
        return rejectWithValue(res?.error || "Server error");

      let mapped = [];

      // 🟢 "with item" record type - Transform data like purchase record
      if (
        selRecord === "with item" &&
        !["By Item", "By Tag", "By Group", "By Category"].includes(selFilter)
      ) {
        mapped = res.data.flatMap((sale) =>
          (sale.details || []).map((item, idx) => {
            const total = Number(sale.total_amount || 0);
            const paid = Number(sale.paid_total || 0);
            const due = Number(sale.due_total || total - paid);

            // Process serials
            let serials = [];
            if (item.is_serial === "yes" && item.serials) {
              serials =
                typeof item.serials === "string"
                  ? item.serials
                    .split(",")
                    .filter(Boolean)
                    .map((slNo) => ({ serial_number: slNo }))
                  : item.serials;
            }

            return {
              id: `${sale.sale_id}-${item.item_id}-${idx}`,
              key: `${sale.sale_id}-${item.item_id}-${idx}`,
              sale_id: sale.sale_id,
              sale_voucher_no: sale.sale_voucher_no,
              created_date: sale.created_date,
              acc_name: sale.acc_name,
              employee_name: sale.employee_name,
              full_name: sale.full_name,
              item_name: item.item_name,
              is_locked: 0,

              display_text: item.display_text,
              item_rate: Number(item.item_rate || 0).toFixed(2),
              item_qty: Number(item.item_qty || 0),
              item_discount_per: item.item_discount_per,
              item_tax_per: item.item_tax_per,
              item_total: Number(item.item_total || 0).toFixed(2),
              other_service_cost: sale.other_service_cost,
              // Financial fields from master
              sub_total: Number(sale.sub_total || 0).toFixed(2),
              total_discount: Number(sale.total_discount || 0).toFixed(2),
              total_tax: Number(sale.total_tax || 0).toFixed(2),
              total_amount: total.toFixed(2),
              paid_total: paid.toFixed(2),
              due_total: due.toFixed(2),

              // Unit information
              unit_name: item.unit_name,
              unit_symbol: item.unit_symbol,
              base_unit_name: item.base_unit_name,
              retail_qty: Number(item.retail_qty || 0),
              conversion: Number(item.conversion || 1),
              is_serial: item.is_serial,
              serials: serials,
              insurance_id: sale.insurance_id || "",
              cgst_amount: sale.cgst_amount,
              sgst_amount: sale.sgst_amount,
              igst_amount: sale.igst_amount,
              // Quantity display
              qty_display: `${item.item_qty || 0} ${item.unit_symbol || ""}${item.conversion > 1
                ? `, ${item.retail_qty || 0} ${item.base_unit_name || ""}`
                : ""
                }`,

              // ✅ CRITICAL: Add unitDetails for expandable rows (like purchase record)
              unitDetails: `Total : Master U. Qty = ${item.item_qty || 0
                }, Base U. Qty = ${item.retail_qty || 0}`,
              tatmeen_sent_status: sale.tatmeen_sent_status,
              sscc_code: sale.sscc_code,
              tatmeen_message_id: sale.tatmeen_message_id,
              tatmeen_retry_count: sale.tatmeen_retry_count,
              tatmeen_failure_reason: sale.tatmeen_failure_reason,
              tatmeen_sscc_status: sale.tatmeen_sscc_status,
              tatmeen_manual_action_required:
                sale.tatmeen_manual_action_required,
              tatmeen_last_retry_at:
                sale.tatmeen_last_retry_at,
            };
          })
        );

        dispatch(setDetails(res.data || [])); // Store original structure
        dispatch(setList(mapped)); // Store transformed data for table
      }
      // 🟢 Item-based filters - Transform data
      else if (
        ["By Item", "By Tag", "By Group", "By Category"].includes(selFilter)
      ) {
        mapped = res.data.map((item, idx) => {
          // Process serials
          let serials = [];
          if (item.is_serial === "yes" && item.serials) {
            serials =
              typeof item.serials === "string"
                ? item.serials
                  .split(",")
                  .filter(Boolean)
                  .map((slNo) => ({ serial_number: slNo }))
                : item.serials;
          }

          return {
            id: `${item.sale_id}-${item.item_id}-${idx}`,
            key: `${item.sale_id}-${item.item_id}-${idx}`,
            sale_id: item.sale_id,
            sale_voucher_no: item.sale_voucher_no,
            created_date: item.created_date,
            acc_name: item.acc_name,
            employee_name: item.employee_name,
            item_name: item.item_name,
            display_text: item.display_text,
            item_rate: Number(item.item_rate || 0).toFixed(2),
            item_qty: Number(item.item_qty || 0),
            item_discount_per: item.item_discount_per,
            item_tax_per: item.item_tax_per,
            item_total: Number(item.item_total || 0).toFixed(2),
            is_locked: 0,

            // Additional fields for specific filter types
            group_name: item.group_name,
            category_name: item.category_name,
            tag_name: item.tag_name,
            field_name: item.field_name,

            // Unit information
            unit_name: item.unit_name,
            unit_symbol: item.unit_symbol,
            base_unit_name: item.base_unit_name,
            retail_qty: Number(item.retail_qty || 0),
            conversion: Number(item.conversion || 1),
            is_serial: item.is_serial,
            serials: serials,

            // Quantity display
            qty_display: `${item.item_qty || 0} ${item.unit_symbol || ""}${item.conversion > 1
              ? `, ${item.retail_qty || 0} ${item.base_unit_name || ""}`
              : ""
              }`,

            // ✅ CRITICAL: Add unitDetails for expandable rows
            unitDetails: `Total : Master U. Qty = ${item.item_qty || 0
              }, Base U. Qty = ${item.retail_qty || 0}`,
          };
        });

        dispatch(setDetails(mapped));
        dispatch(setList(mapped));
      }
      // 🟢 "without item" record type
      else {
        mapped = res.data.map((sale, idx) => {
          const total = Number(sale.total_amount || 0);
          const paid = Number(sale.paid_total || 0);
          const due = Number(sale.due_total || total - paid);
          const modeOfPay = sale.trans?.[0]?.to_acc_name || "-";

          return {
            id: sale.sale_id,
            key: idx,
            sale_id: sale.sale_id,
            sale_voucher_no: sale.sale_voucher_no,
            created_date: sale.created_date,
            acc_name: sale.acc_name,
            employee_name: sale.employee_name,
            full_name: sale.full_name,
            mode_of_pay: modeOfPay,
            sub_total: Number(sale.sub_total || 0).toFixed(2),
            total_discount: Number(sale.total_discount || 0).toFixed(2),
            total_tax: Number(sale.total_tax || 0).toFixed(2),
            other_service_cost: Number(sale.other_service_cost || 0).toFixed(2),

            cgst_amount: sale.cgst_amount,
            sgst_amount: sale.sgst_amount,
            igst_amount: sale.igst_amount,

            total_amount: total.toFixed(2),
            paid_total: paid.toFixed(2),
            due_total: due.toFixed(2),
            tatmeen_sent_status: sale.tatmeen_sent_status,
            sscc_code: sale.sscc_code,
            tatmeen_message_id: sale.tatmeen_message_id,
            tatmeen_retry_count: sale.tatmeen_retry_count,
            tatmeen_failure_reason: sale.tatmeen_failure_reason,
            tatmeen_sscc_status: sale.tatmeen_sscc_status,
            tatmeen_manual_action_required: sale.tatmeen_manual_action_required,
            tatmeen_last_retry_at: sale.tatmeen_last_retry_at,
            insurance_id: sale.insurance_id || "",
            is_locked: sale?.is_locked,
          };
        });

        dispatch(setList(mapped));
        dispatch(setDetails([]));
      }

      return mapped;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  }
);

// 🔴 Delete sale thunk
export const deleteSale = createAsyncThunk(
  "salesRecord/deleteSale",
  async (saleId, { dispatch, getState, rejectWithValue }) => {
    try {
      const res = await postData("/sales/sale-delete", { sale_id: saleId });
      if (res?.error) {
        return rejectWithValue(res.message);
      }

      const { filters } = getState().salesRecord;

      dispatch(fetchSalesRecord({ filters }));
      return res?.message || "Deleted successfully";
    } catch (err) {
      console.log(err, "err");
      return rejectWithValue(err?.response?.data || err.message);
    }
  }
);


export const retryTatmeenSales = createAsyncThunk(
  "salesRecord/retryTatmeen",
  async (sale_id, { rejectWithValue }) => {
    try {
      const res = await postData(
        "/sales/sales-manual-retry",
        { sale_id }
      );

      console.log("postData response:", res);  // ← TEMP LOG

      if (res?.data?.error) {
        return rejectWithValue(res.data.message || "Retry failed");
      }

      return res?.data || res;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message ||
        err.response?.data ||
        err.message
      );
    }
  }
);

